// Keyboard-strip geometry shared by the diagram and its consumers — kept
// out of PianoKeyboardDiagram.tsx so that file exports only a component
// (fast refresh).
//
// THE WINDOW IS THE VOICING'S (user ruling Sep 2026, against iReal again:
// "on both ours there is redundant space… make ours narrower by removing
// unused keys, not all though, still some padding"): the strip runs from the
// lowest sounding key to the highest plus KEY_PAD white keys either side —
// no octave snapping, no two-octave floor — so a close voicing is a short
// strip and a two-hand voicing a long one, and the key size is a property of
// the SHEET, not of how many keys a chord happens to span. That needs the
// viewBox to follow the key count (KEY_W per white key, H fixed) instead of
// squeezing every window into 100 units: consumers size the strip by HEIGHT
// (the `height` prop, or the sheet's `--keyboard-height`) and the width
// follows, `--keyboard-aspect` carrying the ratio for CSS. The keys are also
// flatter than before (H 42 → 30 at the old key width): iReal's strip is
// low, and at a full bar's width ours stood 113px tall on a desktop sheet.

const WHITE_PC = [0, 2, 4, 5, 7, 9, 11];
export const IS_BLACK = [
  false,
  true,
  false,
  true,
  false,
  false,
  true,
  false,
  true,
  false,
  true,
  false,
];

/** One white key's width in viewBox units — fifteen to the old 100-wide
 * two-octave strip, so a two-octave window is exactly the size it was. */
export const KEY_W = 100 / 15;
/** The strip's height in viewBox units (the white keys' length). */
export const KEYBOARD_H = 30;
/** White keys of padding either side of the sounding span. */
export const KEY_PAD = 2;
/** Height ÷ width of the nominal two-octave strip — the lane a consumer
 * reserves when it does not yet know the voicing. */
export const KEYBOARD_NOMINAL_ASPECT = KEYBOARD_H / (15 * KEY_W);

export const isWhite = (midi: number) => WHITE_PC.includes(((midi % 12) + 12) % 12);

/** Step `count` white keys from `midi` (itself a white key) in `dir`. */
function stepWhites(midi: number, count: number, dir: 1 | -1) {
  let m = midi;
  for (let i = 0; i < count; i += 1) {
    m += dir;
    while (!isWhite(m)) m += dir;
  }
  return m;
}

/** The display range: the sounding span widened to whole white keys and
 * padded by KEY_PAD white keys either side. A black key at the edge of the
 * span takes the white key beyond it first, so it is never half drawn. Both
 * bounds always land on white keys — a strip ends on a whole key, like
 * every drawn keyboard. Explicit bounds pass through untouched. */
export function keyboardRange(midis: number[], low?: number, high?: number) {
  const min = midis.length ? Math.min(...midis) : 60;
  const max = midis.length ? Math.max(...midis) : 72;
  let lo = low;
  if (lo === undefined) {
    lo = min;
    while (!isWhite(lo)) lo -= 1;
    lo = stepWhites(lo, KEY_PAD, -1);
  }
  let hi = high;
  if (hi === undefined) {
    hi = max;
    while (!isWhite(hi)) hi += 1;
    hi = stepWhites(hi, KEY_PAD, 1);
  }
  return { low: lo, high: hi };
}

export function whitesIn(range: { low: number; high: number }) {
  const whites: number[] = [];
  for (let m = range.low; m <= range.high; m += 1) if (isWhite(m)) whites.push(m);
  return whites;
}

/** Width ÷ height of the strip a voicing draws — what a consumer that lays
 * the diagram out by height needs to know its width. */
export function keyboardAspect(midis: number[], low?: number, high?: number) {
  return (whitesIn(keyboardRange(midis, low, high)).length * KEY_W) / KEYBOARD_H;
}
